import "./styles.css";
import { ControlInputFieldProps } from "components/Form/typings";

import Trix from "trix";
import { useRef } from "react";
import lodash from "lodash";
import { useFormState } from "react-hook-form";
import useModal from "hooks/useModal";
import { LegacyTargetType } from "features/RecordTarget/models";
import { ActionType } from "features/Resources/Shared/models";
import { FloatingField } from "design-system";
import PersonalizationTokenMenu from "./PersonalizationTokenMenu";
import { RichText } from "../TrixTextAreaField/trixrichtext";

type TemplateRichTextAreaFieldProps = ControlInputFieldProps & {
  actionType: ActionType;
  targetType?: LegacyTargetType;
  hideToolbar?: boolean;
  alwaysShowToolbar?: boolean;
  disabled?: boolean;
  /** When true, the FloatingField host renders transparent — for use inside a
   *  ComposeBox or anywhere the surface already owns its background. Also
   *  swaps the floating label for a label-less layout. */
  transparent?: boolean;
  /** Drop the heading/size button from the toolbar. Defaults true for
   *  action_type=message (Slack doesn't render headings) — set explicitly
   *  for other surfaces that should also exclude it (e.g. task descriptions). */
  hideHeading?: boolean;
  /** Placeholder text shown inside the editor when empty (e.g. "Write the task description…"). */
  placeholder?: string;
  /** Override the paperclip (attachments tray) button. Defaults on only when onFileAttach is provided. */
  includeAttachment?: boolean;
  /** Route paperclip/drag files to an attachments tray (enables the paperclip). */
  onFileAttach?: (file: File) => void;
};

export default function TemplateRichTextAreaField(props: TemplateRichTextAreaFieldProps) {
  const {
    open: openPersonalizationTokenModal,
    close: closePersonalizationTokenModal,
    Modal: PersonalizationTokenModal,
  } = useModal({
    headerTitle: "Insert Token",
    headerFontSize: "lg",
    size: "lg",
    isCentered: true,
  });

  const {
    name,
    label,
    control,
    required,
    actionType,
    targetType = undefined,
    hideToolbar,
    alwaysShowToolbar,
    disabled = false,
    transparent = false,
    hideHeading,
    placeholder,
    includeAttachment,
    onFileAttach,
    errors: errorsFromProps,
  } = props;

  // Messages render in Slack which has no heading styling — drop the size
  // button automatically for any message-type Trix. Other action types can
  // opt in by passing `hideHeading` explicitly.
  const effectiveHideHeading = hideHeading ?? actionType === "message";

  // Email bodies render with mail-client default heading sizes — show that
  // scale while authoring so the editor matches what recipients get.
  const emailHeadingSizes = actionType === "email";

  const { errors: formStateErrors } = useFormState({ control });
  const errors = formStateErrors || errorsFromProps || {};
  const errorMessage = lodash.get(errors, name)?.message as string | undefined;

  const trixRef = useRef<Trix>(null);

  return (
    <>
      <FloatingField name={name} errors={errorMessage} required={required} transparent={transparent}>
        {!transparent && <FloatingField.Label>{label}</FloatingField.Label>}
        <FloatingField.Trix
          ref={trixRef}
          control={control}
          name={name}
          isEditable={!disabled}
          hideToolbar={hideToolbar}
          alwaysShowToolbar={alwaysShowToolbar}
          includeAttachment={includeAttachment ?? !!onFileAttach}
          includeInlineImage={actionType !== "message"}
          onFileAttach={onFileAttach}
          hideHeading={effectiveHideHeading}
          emailHeadingSizes={emailHeadingSizes}
          placeholder={placeholder}
          onInitialize={(_: any, richText: RichText) => {
            richText.insertPersonalizationElements();
            richText.personalizationTokenButton.addEventListener("click", () => {
              openPersonalizationTokenModal();
            });
          }}
        />
      </FloatingField>
      <PersonalizationTokenModal>
        <PersonalizationTokenMenu
          actionType={actionType}
          targetType={targetType}
          onSelect={(token) => {
            if (!trixRef.current) return;
            const { editor } = trixRef.current;
            const attachment = new Trix.Attachment({ content: token.content, sgid: token.sgid });
            editor.insertAttachment(attachment);
            closePersonalizationTokenModal();
          }}
        />
      </PersonalizationTokenModal>
    </>
  );
}
